import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Star, Loader2, CheckCircle2, Download } from "lucide-react";
import { trackLeadConversion } from "@/lib/analytics";
import { metaLead } from "@/lib/metaPixel";
import { getAttribution } from "@/lib/attribution";
import { setUserRole } from "@/lib/userRole";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAGNET_ID = "wer-darf-was-atlas";

const ROLES = [
  ["pflegekraft", "Pflegekraft"],
  ["pdl", "PDL / Leitung"],
  ["leitung", "Heim-/GF"],
  ["andere", "Andere"],
];

export default function AtlasWerDarfWas() {
  const [form, setForm] = useState({ name: "", email: "", role: "pflegekraft", company: "", phone: "", newsletter: false });
  const [state, setState] = useState({ loading: false, success: false, error: null });

  useEffect(() => {
    document.title = "Wer darf was? Der Delegations-Atlas für die Pflege | Dickies-Campus";
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
    m.content = "Der Delegations-Atlas für die Pflege: 251 Seiten. Kostenlose 17-Seiten-Vorschau per E-Mail, Vollversion als Fachbuch-PDF (49,99 €) im Shop.";
    window.scrollTo(0, 0);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, success: false, error: null });
    try {
      await axios.post(`${API}/leadmagnet/${MAGNET_ID}`, {
        email: form.email,
        name: form.name,
        role: form.role,
        company: form.company,
        position: "",
        phone: form.phone,
        bundesland: "",
        consent: true,
        newsletter_opt_in: form.newsletter,
        source: "ads-lp-atlas",
        attribution: getAttribution(),
      });
      trackLeadConversion(5, `leadmagnet:${MAGNET_ID}:ads-lp`);
      metaLead(`leadmagnet:${MAGNET_ID}`);
      setUserRole(form.role);
      setState({ loading: false, success: true, error: null });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const msg = status === 400 && typeof detail === "string" ? detail
        : status === 422 ? "Bitte gültige E-Mail eingeben."
        : "Bitte später erneut versuchen.";
      setState({ loading: false, success: false, error: msg });
    }
  };

  const inputCls = "w-full rounded-xl border border-cream-300 bg-white px-4 py-3 text-base text-sand-800 outline-none focus:border-terra placeholder:text-sand-400";

  return (
    <div className="min-h-screen bg-cream-50 px-4 py-6 sm:py-10" data-testid="atlas-lp-page">
      <div className="max-w-md mx-auto">
        <img src="/dh_logo_dark.webp" alt="Dickies-Campus" className="h-10 mx-auto" />

        <img
          src="/leitfaden-wer-darf-was-atlas-preview.webp"
          alt="Wer darf was? Der Delegations-Atlas für die Pflege – Cover"
          data-testid="atlas-lp-cover"
          className="mt-5 w-56 sm:w-64 mx-auto rounded-xl shadow-soft border border-cream-300"
        />

        <h1 className="font-display normal-case text-[1.65rem] sm:text-3xl text-sand-800 leading-[1.15] tracking-tight text-center mt-4">
          Wer darf was? Der Delegations-Atlas für die Pflege
        </h1>

        <ul className="mt-3 space-y-1 text-center text-[14px] text-sand-700" data-testid="atlas-lp-benefits">
          <li>251 Seiten</li>
          <li>Kostenlose 17-Seiten-Vorschau per E-Mail</li>
          <li>Vollversion als Fachbuch-PDF (49,99 €) im Shop</li>
        </ul>

        {state.success ? (
          <div className="mt-5 rounded-2xl bg-white border border-cream-300 shadow-soft p-6 text-center" data-testid="atlas-lp-success">
            <CheckCircle2 className="h-9 w-9 text-sage-dark mx-auto mb-2" />
            <h2 className="font-display normal-case text-xl text-sand-800">Fast geschafft!</h2>
            <p className="text-sm text-sand-600 mt-2">
              Wir haben dir gerade eine E-Mail an <strong className="text-sand-800">{form.email}</strong> geschickt.
              Bitte klicke darin auf den Bestätigungs-Link — danach bekommst du die Vorschau sofort.
            </p>
            <p className="mt-2 text-[12px] text-sand-500">Keine Mail erhalten? Schau bitte auch im Spam-Ordner.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-5 rounded-2xl bg-white border border-cream-300 shadow-soft p-5 space-y-3" data-testid="atlas-lp-form">
            <div className="grid grid-cols-2 gap-2">
              {ROLES.map(([v, l]) => (
                <button key={v} type="button" onClick={() => setForm({ ...form, role: v })}
                  data-testid={`atlas-lp-role-${v}`}
                  className={`rounded-full px-3 h-10 text-[12.5px] font-semibold border transition-all ${
                    form.role === v ? "border-terra bg-terra text-white" : "border-cream-300 text-sand-700 hover:border-clay bg-white"
                  }`}>{l}</button>
              ))}
            </div>
            <input className={inputCls} placeholder="Vor- und Nachname *" value={form.name} required minLength={2}
              onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="atlas-lp-name" />
            <input className={inputCls} type="email" placeholder="E-Mail-Adresse *" value={form.email} required
              onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="atlas-lp-email" />
            <input className={inputCls} placeholder="Pflegedienst / Klinik (optional)" value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="atlas-lp-company" />
            <input className={inputCls} type="tel" inputMode="tel" placeholder="Telefon (z. B. 0211 12345678) *" value={form.phone} required
              onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="atlas-lp-phone" />
            <label className="flex items-start gap-2.5 text-[12px] text-sand-600 leading-snug cursor-pointer">
              <input type="checkbox" checked={form.newsletter}
                onChange={(e) => setForm({ ...form, newsletter: e.target.checked })}
                data-testid="atlas-lp-newsletter" className="mt-0.5 h-4 w-4 accent-terra shrink-0" />
              <span><b>Helden-Briefing abonnieren</b> – 1× pro Monat ein PDL-Update zu MD, Abrechnung &amp; Personal. Abmelden jederzeit.</span>
            </label>
            {state.error && <p className="text-[13px] text-red-600" data-testid="atlas-lp-error">{state.error}</p>}
            <button type="submit" disabled={state.loading} data-testid="atlas-lp-submit"
              className="w-full rounded-full bg-terra hover:bg-terra-dark text-white py-3.5 font-semibold text-[15px] inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
              {state.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Vorschau per E-Mail anfordern
            </button>
            <p className="text-[11px] text-sand-500 leading-snug">
              Mit dem Absenden stimmst du der Verarbeitung deiner Daten gemäß{" "}
              <Link to="/datenschutz" className="underline" target="_blank">Datenschutzerklärung</Link> zu.
              Wir senden dir eine Bestätigungs-Mail — erst nach Klick auf den Link bekommst du die Vorschau.
            </p>
          </form>
        )}

        <div className="mt-4 text-center" data-testid="atlas-lp-trust">
          <div className="inline-flex items-center gap-1.5">
            {[0, 1, 2, 3, 4].map((i) => (
              <Star key={i} className="h-4 w-4 text-saffron fill-saffron" />
            ))}
            <span className="text-[13px] font-semibold text-sand-800 ml-1">4,8</span>
            <span className="text-[13px] text-sand-500">/ 183 Google-Bewertungen</span>
          </div>
        </div>
      </div>
    </div>
  );
}
